import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import {
  fetchDecreeApprovals,
  fetchDecreeApprovalsFailure,
  fetchDecreeApprovalsSuccess,
  fetchDecreeAttachments,
  fetchDecreeAttachmentsFailure,
  fetchDecreeAttachmentsSuccess,
  fetchDecreeComments,
  fetchDecreeCommentsFailure,
  fetchDecreeCommentsSuccess,
  fetchDecreeFinancial,
  fetchDecreeFinancialFailure,
  fetchDecreeFinancialSuccess,
  fetchDecreeGeneral,
  fetchDecreeGeneralFailure,
  fetchDecreeGeneralSuccess,
  fetchDecreeHeader,
  fetchDecreeHeaderFailure,
  fetchDecreeHeaderSuccess,
  fetchDecreeImportedDecree,
  fetchDecreeImportedDecreeFailure,
  fetchDecreeImportedDecreeSuccess,
  fetchDecreeInfo,
  fetchDecreeInfoFailure,
  fetchDecreeInfoSuccess,
  fetchDecreeNotes,
  fetchDecreeNotesFailure,
  fetchDecreeNotesSuccess,
  fetchDecreePrimaryActions,
  fetchDecreePrimaryActionsFailure,
  fetchDecreePrimaryActionsSuccess,
  fetchDecreeRights,
  fetchDecreeRightsFailure,
  fetchDecreeRightsSuccess,
  fetchDecreeSidebar,
  fetchDecreeSidebarFailure,
  fetchDecreeSidebarSuccess,
  fetchDecreeWebcast,
  fetchDecreeWebcastFailure,
  fetchDecreeWebcastSuccess
} from './decrees.actions';
import { IDecree } from './decrees.models';

export interface DecreesState extends EntityState<IDecree> {
  loadingDecree: boolean;
  error?: any;
}

export const decreeAdapter: EntityAdapter<IDecree> = createEntityAdapter<IDecree>();

export const initialState: DecreesState = decreeAdapter.getInitialState({
  // set initial required properties
  loadingDecree: false,

  error: null
});

const reducer = createReducer(
  initialState,
  on(fetchDecreeHeader, (state, { id }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        header: {
          loading: true
        }
      },
      state
    )
  ),
  on(fetchDecreeHeaderSuccess, (state, { id, data }) =>
    decreeAdapter.upsertOne(
      {
        id: id,
        ...state.entities[id],
        header: {
          loading: false,
          ...data
        }
      },
      state
    )
  ),
  on(fetchDecreeHeaderFailure, (state, { id, error }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        header: {
          loading: false,
          error
        }
      },
      state
    )
  ),
  on(fetchDecreeSidebar, (state, { id }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        sidebar: {
          loading: true
        }
      },
      state
    )
  ),
  on(fetchDecreeSidebarSuccess, (state, { id, data }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        sidebar: {
          loading: false,
          items: data
        }
      },
      state
    )
  ),
  on(fetchDecreeSidebarFailure, (state, { id, error }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        sidebar: {
          loading: false,
          error
        }
      },
      state
    )
  ),
  on(fetchDecreePrimaryActions, (state, { id }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        primaryActions: {
          loading: true
        }
      },
      state
    )
  ),
  on(fetchDecreePrimaryActionsSuccess, (state, { id, data }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        primaryActions: {
          loading: false,
          items: data
        }
      },
      state
    )
  ),
  on(fetchDecreePrimaryActionsFailure, (state, { id, error }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        primaryActions: {
          loading: false,
          error
        }
      },
      state
    )
  ),
  on(fetchDecreeGeneral, (state, { id }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        general: {
          loading: true
        }
      },
      state
    )
  ),
  on(fetchDecreeGeneralSuccess, (state, { id, data }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        general: {
          loading: false,
          ...data
        }
      },
      state
    )
  ),
  on(fetchDecreeGeneralFailure, (state, { id, error }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        general: {
          loading: false,
          error
        }
      },
      state
    )
  ),
  on(fetchDecreeAttachments, (state, { id }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        attachments: {
          loading: true
        }
      },
      state
    )
  ),
  on(fetchDecreeAttachmentsSuccess, (state, { id, data }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        attachments: {
          loading: false,
          ...data
        }
      },
      state
    )
  ),
  on(fetchDecreeAttachmentsFailure, (state, { id, error }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        attachments: {
          loading: false,
          error
        }
      },
      state
    )
  ),
  on(fetchDecreeComments, (state, { id }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        comments: {
          loading: true
        }
      },
      state
    )
  ),
  on(fetchDecreeCommentsSuccess, (state, { id, data }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        comments: {
          loading: false,
          ...data
        }
      },
      state
    )
  ),
  on(fetchDecreeCommentsFailure, (state, { id, error }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        comments: {
          loading: false,
          error
        }
      },
      state
    )
  ),
  on(fetchDecreeApprovals, (state, { id }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        approvals: {
          loading: true
        }
      },
      state
    )
  ),
  on(fetchDecreeApprovalsSuccess, (state, { id, data }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        approvals: {
          loading: false,
          ...data
        }
      },
      state
    )
  ),
  on(fetchDecreeApprovalsFailure, (state, { id, error }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        approvals: {
          loading: false,
          error
        }
      },
      state
    )
  ),
  on(fetchDecreeInfo, (state, { id }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        info: {
          loading: true
        }
      },
      state
    )
  ),
  on(fetchDecreeInfoSuccess, (state, { id, data }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        info: {
          loading: false,
          ...data
        }
      },
      state
    )
  ),
  on(fetchDecreeInfoFailure, (state, { id, error }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        info: {
          loading: false,
          error
        }
      },
      state
    )
  ),
  on(fetchDecreeRights, (state, { id }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        rights: {
          loading: true
        }
      },
      state
    )
  ),
  on(fetchDecreeRightsSuccess, (state, { id, data }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        rights: {
          loading: false,
          ...data
        }
      },
      state
    )
  ),
  on(fetchDecreeRightsFailure, (state, { id, error }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        rights: {
          loading: false,
          error
        }
      },
      state
    )
  ),
  on(fetchDecreeFinancial, (state, { id }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        financial: {
          loading: true
        }
      },
      state
    )
  ),
  on(fetchDecreeFinancialSuccess, (state, { id, data }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        financial: {
          loading: false,
          ...data
        }
      },
      state
    )
  ),
  on(fetchDecreeFinancialFailure, (state, { id, error }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        financial: {
          loading: false,
          error
        }
      },
      state
    )
  ),
  on(fetchDecreeNotes, (state, { id }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        notes: {
          loading: true
        }
      },
      state
    )
  ),
  on(fetchDecreeNotesSuccess, (state, { id, data }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        notes: {
          loading: false,
          ...data
        }
      },
      state
    )
  ),
  on(fetchDecreeNotesFailure, (state, { id, error }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        notes: {
          loading: false,
          error
        }
      },
      state
    )
  ),
  on(fetchDecreeWebcast, (state, { id }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        webcast: {
          loading: true
        }
      },
      state
    )
  ),
  on(fetchDecreeWebcastSuccess, (state, { id, data }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        webcast: {
          loading: false,
          ...data
        }
      },
      state
    )
  ),
  on(fetchDecreeWebcastFailure, (state, { id, error }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        webcast: {
          loading: false,
          error
        }
      },
      state
    )
  ),
  on(fetchDecreeImportedDecree, (state, { decreeId }) =>
    decreeAdapter.upsertOne(
      {
        id: decreeId,
        ...state.entities[decreeId],
        importedDecree: {
          loading: true
        }
      },
      state
    )
  ),
  on(fetchDecreeImportedDecreeSuccess, (state, { id, data }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        importedDecree: {
          loading: false,
          ...data
        }
      },
      state
    )
  ),
  on(fetchDecreeImportedDecreeFailure, (state, { id, error }) =>
    decreeAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        importedDecree: {
          loading: false,
          error
        }
      },
      state
    )
  )
);
export function decreesReducer(state: DecreesState | undefined, action: Action) {
  return reducer(state, action);
}
